import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useHistory } from 'react-router-dom';
import translate from '../translate';
import Modal from './Modal';
import AcademySideBar from './components/AcademySideBar';
import PriceProp from '../propTypes/priceProp';
import ImageProp from '../propTypes/imageProp';
import {
  compose,
  findCurrencySymbol,
  handleBillingAddressInOrder,
  handleInitTab,
  PAYMENT_METHOD,
  returnAddress,
} from '../../services/utilityFunctions';
import Notifications from '../../services/notifications';
import withCSRF from '../withCSRF';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import CurrencyProp from '../../sections/profile/propTypes/currencyProp';
import Loader from '../../services/loader';
import CountryProp from '../forms/Address/CountryProp';
import StripeService from '../../services/stripeService';
import TenantProp, { PAYMENT_PROVIDER } from '../propTypes/tenantProp';
import VatTypesProp from '../forms/Address/VatTypesProp';
import {
  getCreditCardList,
  handlePaypalPayment,
} from '../../services/checkoutHelper';
import AcademyPaymentWizard from './components/AcademyPaymentWizard';
import AcademyOrderDetail from './components/academyPaymentSteps/AcademyOrderDetail';
import BillingPaymentOptions, {
  BILLING_OPTIONS,
} from '../shop/BillingPaymentOptions';
import CheckoutPayment from './components/CheckoutPayment';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const paymentReducer = (state, action) => {
  switch (action.type) {
    case 'changeTab': {
      return { ...state, tabActive: action.payload.tab };
    }
    case 'changeFormCard': {
      return { ...state, addingNewCard: action.payload.addingNewCard };
    }
    case 'companyBilling': {
      return { ...state, companyBilling: action.payload };
    }
    case 'updatedUserCards': {
      return {
        ...state,
        userCreditCards: action.payload.cards,
        addingNewCard: false,
        selectedCreditCard: action.payload.cardId,
      };
    }
    case 'removeCreditCard': {
      const { cards } = action.payload;
      let returnedState = {
        ...state,
        userCreditCards: cards,
        addingNewCard: false,
      };
      const cardStillExist = cards.find(
        c => c._id === state.selectedCreditCard,
      );
      if (!cardStillExist) {
        returnedState = {
          ...returnedState,
          selectedCreditCard: null,
        };
      }
      return returnedState;
    }
    case 'updatedCardSelected': {
      return {
        ...state,
        selectedCreditCard: action.payload.cardId,
      };
    }
    case 'updateCards': {
      return {
        ...state,
        userCreditCards: action.payload.cards,
        selectedCreditCard: action.payload.cardId,
      };
    }
    case 'updateHolderName': {
      return {
        ...state,
        accountHolderName: action.payload.accountHolderName,
      };
    }
    default:
      console.log('ERROR - Payment Reducer');
  }
  return null;
};
const initialStatePayment = {
  accountHolderName: '',
  selectedCreditCard: null,
  userCreditCards: [],
};

const couponReducer = (state, action) => {
  switch (action.type) {
    case 'couponSelection': {
      return {
        ...state,
        selectedCoupon:
          state.selectedCoupon === action.payload ? '' : action.payload,
        couponInserted: '',
      };
    }
    case 'clearCoupon': {
      return {
        ...state,
        selectedCoupon: '',
        couponInserted: '',
      };
    }
    case 'couponInserted': {
      return {
        ...state,
        couponInserted: action.payload,
        selectedCoupon: '',
      };
    }
    case 'updateCouponList': {
      return {
        ...state,
        coupons: action.payload,
      };
    }
    default:
      console.log('ERROR - Coupon Reducer');
  }
  return null;
};
const initialStateCoupon = {
  selectedCoupon: '',
  couponInserted: '',
  coupons: [],
};

export const addressReducer = (state, action) => {
  switch (action.type) {
    case 'updateAddress': {
      return {
        ...state,
        billingAddress: {
          ...state.billingAddress,
          ...action.payload,
        },
      };
    }
    case 'showAddressForm': {
      return {
        ...state,
        showAddressForm: action.payload,
      };
    }
    case 'updateFullAddress': {
      return {
        ...state,
        billingAddress: action.payload,
        countryCode: action.payload.countryCode,
      };
    }
    default:
      console.log('ERROR - Address Reducer');
  }
  return null;
};
const initialStateAddress = {
  billingAddress: {},
  showAddressForm: false,
};

const checkDiscountToApply = checkoutAcademyCoupon => {
  const existCoupon =
    checkoutAcademyCoupon?.couponInserted?.length > 0 ||
    checkoutAcademyCoupon?.selectedCoupon?.length > 0;
  if (!existCoupon) {
    return {
      isPaidWithCoins: false,
      discount: null,
    };
  }
  const coupon =
    checkoutAcademyCoupon.couponInserted.length > 0
      ? checkoutAcademyCoupon.couponInserted
      : checkoutAcademyCoupon.selectedCoupon;
  const isCoins = coupon.toUpperCase() === 'COINS';
  return {
    isPaidWithCoins: isCoins,
    discount: !isCoins ? coupon : null,
  };
};

const checkIfShouldReload = (currentCountry, previousCountry) =>
  currentCountry !== previousCountry;

export const handleRulesCompanyBilling = (companyBilling, isPhoneRequired) => {
  const {
    emails = [],
    name,
    taxCode,
    taxType,
    vatNumber,
    address,
    email = '',
    phone = '',
  } = companyBilling;
  const errors = [];
  const emailErrors = [];
  const emailsToTest = [email, ...emails];
  if (emailsToTest?.length === 0) errors.push({ emails: true });
  if (emailsToTest.length > 0) {
    emailsToTest.map((e, index) => {
      if (!emailRegex.test(e)) {
        emailErrors.push(index);
      }
      return e;
    });
  }

  if (!name || name.length === 0) errors.push({ name: true });
  if (!taxCode || !taxType) errors.push({ taxCode: true });
  if (!vatNumber || vatNumber.length === 0) errors.push({ vatNumber: true });
  if (isPhoneRequired && (!phone || phone.length === 0)) {
    errors.push({ phone: true });
  }
  if (!address?.address_1 || address?.address_1?.length === 0) {
    errors.push({ address_1: true });
  }
  if (!address?.zipCode || address?.zipCode?.length === 0) {
    errors.push({ zipCode: true });
  }
  if (!address?.city || address?.city?.length === 0) {
    errors.push({ city: true });
  }
  if (!address?.countryCode || address?.countryCode?.length === 0) {
    errors.push({ countryCode: true });
  }
  return { errors, emailErrors };
};

const checkForDuplicatedEmails = emails => {
  // Create an object to store email addresses as keys and their indices as values
  const emailIndices = {};

  // Initialize an array to store duplicates
  const duplicatedEmails = [];

  // Iterate through the emailList and check for duplicates
  emails.forEach((u, index) => {
    if (emailIndices[u.email] !== undefined) {
      // Email is a duplicate, store its index
      duplicatedEmails.push({
        email: u.email,
        indices: [emailIndices[u.email], index],
      });
    } else {
      // Email is not a duplicate, add it to the object
      emailIndices[u.email] = index;
    }
  });

  return duplicatedEmails;
};

function AcademyPaymentModal({
  t,
  _csrf,
  onClose,
  item,
  typeOfItem,
  currencies,
  countries,
  orderId: order,
  redirectStatus,
  paymentIntent,
  tenant,
  vatTypes,
  openSuccessModal,
}) {
  const isDirectSellingActive =
    tenant && tenant.settings && tenant.settings.directSelling;

  const browserHistory = useHistory();
  const { settings = {} } = tenant;
  const { paymentMethods = [] } = settings;
  const [orderCreated, setOrderCreated] = useState(order || null);
  // INIT PROPS
  const { loggedUser } = useContext(LoggedUserContext);
  const {
    personalInfo,
    address,
    shippingCountry,
    email,
    _id: currentUserId,
  } = loggedUser;
  let country = (address ? address.countryCode : shippingCountry) || null;
  const currency = (loggedUser && loggedUser.currency) || 'eur';
  const { fieldId, field } = typeOfItem;
  const [step, setStep] = useState(1);

  const isOnlyPaidByCompany =
    paymentMethods?.length === 1 &&
    paymentMethods.includes(PAYMENT_METHOD.PAID_BY_COMPANY);
  const [billingActive, setBillingActive] = useState(
    !isOnlyPaidByCompany
      ? BILLING_OPTIONS.YOUR_BILLING
      : BILLING_OPTIONS.COMPANY_BILLING,
  );

  const [errors, setErrors] = useState({
    targetUsers: [],
    companyBilling: [],
    duplicatedEmails: [],
    emailsAlreadyInSession: [],
  });

  /** LOADER */
  const loader = useRef(new Loader('#academySideBar'));
  const loaderModal = useRef(new Loader('#academyPayment'));
  const [isLoading, setIsLoading] = useState({ sidebar: false, modal: false });
  useEffect(() => {
    if (loader.current) {
      if (isLoading.sidebar) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
    if (loaderModal.current) {
      if (isLoading.modal) {
        loaderModal.current.show();
      } else {
        loaderModal.current.hide();
      }
    }
  }, [isLoading]);

  const handleIsLoading = loading => {
    setIsLoading(prevState => ({
      ...prevState,
      modal: loading,
    }));
  };

  /** PAYMENT SECTION */
  const [checkoutAcademyPayment, dispatchAcademyPayment] = useReducer(
    paymentReducer,
    initialStatePayment,
    () => ({
      ...initialStatePayment,
      tabActive: null,
      companyBilling: {
        name: '',
        phone: '',
        taxCode: '',
        taxType: '',
        vatNumber: '',
        address: {
          country: '',
          address_1: '',
          address_2: '',
          city: '',
          zipCode: '',
        },
      },
    }),
  );

  useEffect(() => {
    if (!checkoutAcademyPayment?.tabActive && step === 3) {
      dispatchAcademyPayment({
        type: 'changeTab',
        payload: { tab: handleInitTab(paymentMethods) },
      });
    }
  }, [step]);
  const {
    tabActive,
    accountHolderName,
    selectedCreditCard,
    userCreditCards,
    companyBilling,
  } = checkoutAcademyPayment;
  const [companyEmails, setCompanyEmails] = useState({
    email: '',
    emails: [],
  });

  const handleCompanyEmails = (type, value) => {
    setCompanyEmails(prevState => ({
      ...prevState,
      [type]: value,
    }));
  };

  const updatePaymentMethod = (type, payload) => {
    dispatchAcademyPayment({ type, payload });
  };

  const handleBillingChange = billingOption => {
    setBillingActive(billingOption);
    const newPaymentTab =
      billingOption === BILLING_OPTIONS.YOUR_BILLING
        ? paymentMethods[0]
        : PAYMENT_METHOD.PAID_BY_COMPANY;
    dispatchAcademyPayment({
      type: 'changeTab',
      payload: { tab: newPaymentTab },
    });
  };

  const handleChangeCompanyBilling = value => {
    dispatchAcademyPayment({ type: 'companyBilling', payload: value });
  };

  useEffect(() => {
    const getCardsList = async () => {
      const cardsList = await getCreditCardList(_csrf, true, true);
      const defaultCard =
        cardsList && cardsList.length > 0
          ? cardsList.find(c => c.isDefault)?.id || null
          : null;
      dispatchAcademyPayment({
        type: 'updatedUserCards',
        payload: { cards: cardsList, cardId: defaultCard },
      });
    };
    getCardsList();
  }, [dispatchAcademyPayment, _csrf]);

  const getParticipants = useCallback(async emails => {
    try {
      const { data: response } = await axios.get(
        `/api/access-session/users/${item._id}/checkUsers`,
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
          },
          params: { emails: emails.map(e => e.email.trim()) },
        },
      );
      return response.data;
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'report.certificates.error.zip');
      return [null];
    }
  }, []);

  /** COUPON SECTION */
  const [checkoutAcademyCoupon, dispatchCouponSection] = useReducer(
    couponReducer,
    initialStateCoupon,
  );
  const { selectedCoupon, couponInserted, coupons } = checkoutAcademyCoupon;

  const updateCoupon = (type, payload) => {
    dispatchCouponSection({ type, payload });
  };

  const handleAvailableCouponChange = couponList => {
    dispatchCouponSection({ type: 'updateCouponList', payload: couponList });
  };

  const handleClearCoupon = () => {
    dispatchCouponSection({ type: 'clearCoupon' });
  };

  /** ADDRESS SECTION */
  const isIndividual =
    personalInfo?.businessType === 'individual' || !personalInfo?.businessType;
  const companyName = personalInfo?.companyName || null;
  const initialAddress = handleBillingAddressInOrder(
    loggedUser,
    loggedUser?.address?.address_1 ? loggedUser.address : {},
  );
  const [checkoutAcademyAddress, dispatchAddressSection] = useReducer(
    addressReducer,
    initialStateAddress,
    () => ({
      ...initialStateAddress,
      billingAddress: {
        ...initialAddress,
        companyName: !isIndividual ? companyName : null,
      },
      countryCode: initialAddress.countryCode,
    }),
  );
  const { billingAddress, showAddressForm } = checkoutAcademyAddress;
  const handleAddressForm = show =>
    dispatchAddressSection({ type: 'showAddressForm', payload: show });

  const handleAddressChange = fieldParam => value => {
    dispatchAddressSection({
      type: 'updateAddress',
      payload: {
        [fieldParam]: value,
      },
    });
  };

  const saveAddress = () => {
    setIsLoading(prevState => ({ ...prevState, modal: true }));
    const params = {
      address: { ...billingAddress, address: returnAddress(billingAddress) },
      _csrf,
    };
    let userToUpdate = {};
    if (params.address.firstName) {
      userToUpdate.firstName = params.address.firstName;
    }
    if (params.address.lastName) {
      userToUpdate.lastName = params.address.lastName;
    }
    if (params.address.email) {
      userToUpdate.email = params.address.email.toLowerCase();
    }
    if (params.address.phone) {
      userToUpdate.phone = params.address.phone;
    }
    if (params?.address?.companyName) {
      userToUpdate = {
        ...userToUpdate,
        personalInfo: {
          ...loggedUser.personalInfo,
          companyName: params.address.companyName,
        },
      };
    }
    axios({
      url: '/api/user/me',
      method: 'PUT',
      data: { ...params, ...userToUpdate },
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Organization',
      },
    })
      .then(({ data: response }) => {
        // Manage the different stripe configurations
        const { countryCode } = response.data.address;
        const { countryCode: prevCountryCode } = checkoutAcademyAddress;
        const shouldReload = checkIfShouldReload(
          countryCode.toUpperCase(),
          prevCountryCode?.toUpperCase(),
        );

        if (shouldReload) {
          Notifications.showNotificationWarning(
            t('warning'),
            t('warning.updating.stripeAccount'),
          );
          browserHistory.replace(`${window.location.pathname}?open=true`);
          browserHistory.go(0);
        } else {
          const addressUpdated = response.data.address;
          dispatchAddressSection({
            type: 'updateFullAddress',
            payload: addressUpdated,
          });
          handleAddressForm(false);
          setIsLoading(prevState => ({ ...prevState, modal: false }));
          Notifications.showNotificationSuccess(
            t('success'),
            t('checkout.address.success'),
          );
        }
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'checkout.address.error');
        console.log(err);
        setIsLoading(prevState => ({ ...prevState, modal: false }));
      });
  };

  /** ORDER */
  const [checkoutDetail, setCheckoutDetail] = useState({
    subtotal: 0,
    symbol: '€',
    discount: 0,
    tax: 0,
    totalVA: 0,
  });
  // console.log('*checkoutDetail - State (Sidebar)', checkoutDetail);
  // NOTES
  const [notes, setNotes] = useState('');
  const [targetUsers, setTargetUsers] = useState([]);
  const [currentUserBoughtSession, setCurrentUserBoughtSession] =
    useState(false);

  const getCurrentParticipants = params =>
    new Promise((resolve, reject) => {
      axios
        .get(
          `/api/access-session/users/list/${item._id}?participantStatus=participants`,
          {
            params,
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
            },
          },
        )
        .then(({ data }) => {
          resolve(data.data);
        })
        .catch(err => {
          console.log(err);
          reject(err);
        });
    });

  useEffect(() => {
    getCurrentParticipants().then(users => {
      if (users.some(user => currentUserId === user._id)) {
        setTargetUsers([
          {
            email: '',
            firstName: '',
            lastName: '',
          },
        ]);
        setCurrentUserBoughtSession(true);
      } else {
        setTargetUsers([{ email }]);
      }
    });
  }, []);

  const handleTargetEmailsChange = users => {
    setTargetUsers(users);
    setErrors({
      targetUsers: [],
      companyBilling: [],
      duplicatedEmails: [],
      emailsAlreadyInSession: [],
    });
  };
  const handleChangeNotes = event => {
    setNotes(event.target.value);
  };

  const handleStepChange = async futureStep => {
    const emailErrors = [];
    if (futureStep === 2) {
      targetUsers.map((targetUser, index) => {
        if (!emailRegex.test(targetUser.email)) {
          emailErrors.push(index);
        }
        return targetUser;
      });
      const duplicatedEmails = checkForDuplicatedEmails(targetUsers) || [];
      let emailsAlreadyInSession = [];
      if (typeOfItem.fieldId === 'session' && item.mode === 'public') {
        emailsAlreadyInSession = await getParticipants(targetUsers);
      }
      if (
        emailErrors.length > 0 ||
        duplicatedEmails.length > 0 ||
        emailsAlreadyInSession.length > 0
      ) {
        setErrors(prevState => {
          const errorItems = {};
          if (duplicatedEmails.length > 0) {
            errorItems.duplicatedEmails = duplicatedEmails;
          }
          if (emailErrors.length > 0) {
            errorItems.targetUsers = emailErrors;
          }
          if (emailsAlreadyInSession.length > 0) {
            errorItems.emailsAlreadyInSession = emailsAlreadyInSession;
          }
          return { ...prevState, ...errorItems };
        });
        return;
      }
    }
    if (futureStep === 3) {
      if (
        billingActive === BILLING_OPTIONS.COMPANY_BILLING &&
        (handleRulesCompanyBilling({ ...companyBilling, ...companyEmails })
          ?.emailErrors?.length > 0 ||
          handleRulesCompanyBilling({ ...companyBilling, ...companyEmails })
            ?.errors?.length > 0)
      ) {
        setErrors(prevState => ({
          ...prevState,
          companyBilling: handleRulesCompanyBilling({
            ...companyBilling,
            ...companyEmails,
          }),
        }));
        return;
      }
      if (
        billingActive === BILLING_OPTIONS.COMPANY_BILLING &&
        !(
          handleRulesCompanyBilling({ ...companyBilling, ...companyEmails })
            ?.emailErrors?.length > 0 ||
          handleRulesCompanyBilling({ ...companyBilling, ...companyEmails })
            ?.errors?.length > 0
        )
      ) {
        setErrors(prevState => ({ ...prevState, companyBilling: [] }));
      }
      if (
        billingActive === BILLING_OPTIONS.COMPANY_BILLING &&
        companyBilling?.taxCode &&
        companyBilling?.address?.countryCode
      ) {
        if (
          vatTypes.find(v => v.taxCode === companyBilling?.taxCode)
            ?.countryCode !==
          countries.find(a => a.code === companyBilling?.address?.countryCode)
            ?.code
        ) {
          Notifications.showNotificationError(
            t('error'),
            t('shop.error.vatType.country'),
          );
          return;
        }
      }

      if (
        billingActive === BILLING_OPTIONS.YOUR_BILLING &&
        (!billingAddress || !billingAddress.address_1)
      ) {
        setErrors(prevState => ({ ...prevState, billingAddress: true }));
        return;
      }
    }
    setStep(futureStep);
  };
  // Terms
  const [terms, setTerms] = useState(false);
  const handleTerms = useCallback(() => {
    setTerms(!terms);
  }, [terms]);

  /** API REQUESTS - Mount component */
  const getAvailableCoupons = () => {
    let availableCouponsReqPath =
      '/api/discount/coupon/academy/available?moduleType=academy';
    if (currency) {
      availableCouponsReqPath += `&currency=${currency}`;
    }
    if (country) {
      availableCouponsReqPath += `&country=${country}`;
    }
    // console.log('*GET COUPON LIST - PARAMS', { [fieldId]: item._id, quantity: 1 });
    // console.log('*GET COUPON LIST - URL', availableCouponsReqPath);
    axios
      .post(
        availableCouponsReqPath,
        {
          [fieldId]: item._id,
          quantity: 1,
        },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      )
      .then(({ data: response }) => {
        // console.log('*GET COUPON LIST - Response', response.data);
        handleAvailableCouponChange(response.data);
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_getAvailableCoupons');
        console.log(err);
      });
  };

  const handleClose = async () => {
    onClose();
  };

  const getOrderDetail = () => {
    const { isPaidWithCoins, discount } = checkDiscountToApply(
      checkoutAcademyCoupon,
    );
    const params = {
      [field]: [
        {
          [fieldId]: item._id,
          quantity:
            field === 'sessions' && targetUsers ? targetUsers.length : 1,
        },
      ],
      isPaidWithCoins,
      discount,
    };

    if (
      companyBilling?.taxCode?.length > 0 &&
      companyBilling?.address?.countryCode?.length > 0
    ) {
      params.companyBilling = { ...companyBilling, ...companyEmails };
      params.paymentMethodType = PAYMENT_METHOD.PAID_BY_COMPANY;
      country = companyBilling?.address?.countryCode;
    }

    if (tabActive) params.paymentMethodType = tabActive;

    const url = `/api/academy/order/calculate?country=${country}&currency=${currency}`;
    // console.log('*DETAIL-PARAMS', params);
    axios
      .post(url, params, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        // Destructing response
        const { data } = response;
        // console.log('*DATA', data);
        const {
          discountTotal,
          pricesWithoutVAT,
          subtotal,
          subtotalTax,
          total,
          totalVA,
          currency: currencyData,
          isPaidWithCoins: isPaidWithCoinsData,
          fee,
        } = data;
        const symbol = findCurrencySymbol(currencyData, currencies);
        // get first course/session/journey (typeOfItem.field can be 'courses', 'sessions' or 'journeys')
        const itemCheckoutDetail = data[typeOfItem.field][0];
        setCheckoutDetail({
          pricesWithoutVAT,
          subtotal,
          tax: subtotalTax,
          total,
          symbol,
          totalVA,
          isPaidWithCoins: isPaidWithCoinsData,
          discount: discountTotal,
          item: itemCheckoutDetail,
          fee,
        });
        setIsLoading(prevState => ({ ...prevState, sidebar: false }));
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'order_detail_fail');
        console.log(err);
        setIsLoading(prevState => ({ ...prevState, sidebar: false }));
      });
  };
  useEffect(() => {
    getAvailableCoupons();
  }, []);

  useEffect(() => {
    setIsLoading(prevState => ({ ...prevState, sidebar: true }));
    getOrderDetail();
  }, [
    selectedCoupon,
    couponInserted,
    checkoutAcademyAddress.billingAddress,
    targetUsers.length,
    companyBilling?.address?.countryCode,
    companyBilling?.taxCode,
    companyBilling?.vatNumber,
    tabActive,
  ]);

  /** API REQUESTS - PAY ORDER
   * CAPTURE PAYMENT - /api/academy/order/${orderId}/capture_payment
   * CLIENT SIDE PAYMENT - check with stripe in client side (browser)
   * CONFIRM PAYMENT - /api/academy/order/${orderId}/confirm_payment
   * */
  const confirmPayment = async (clientSecret, orderId, paymentMethod) => {
    let url = `/api/payment/intent/academy/order/${orderId}/confirm`;
    if (settings?.paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES) {
      url = `/api/academy/order/${orderId}/confirm_payment`;
    }
    // if stripe with invoices and no value in order, we don't call the confirm payment
    if (
      settings?.paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES &&
      checkoutDetail.total === 0
    ) {
      openSuccessModal(PAYMENT_METHOD.CREDIT_CARD, targetUsers);
      return;
    }
    axios
      .post(
        url,
        { paymentIntentId: clientSecret },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'License',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      )
      .then(() => {
        openSuccessModal(
          checkoutDetail.total === 0
            ? PAYMENT_METHOD.CREDIT_CARD
            : paymentMethod,
          targetUsers,
        );
        // onClose();
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'ERROR_PAYMENT_DENIED');
        console.log(err);
        setIsLoading(prevState => ({ ...prevState, modal: false }));
      });
  };

  useEffect(() => {
    if (order && redirectStatus === 'succeeded' && paymentIntent) {
      const getConfirmation = async () => {
        await confirmPayment(paymentIntent, order, order.paymentMethodType);
      };

      getConfirmation();
    } else if (order && redirectStatus === 'failed') {
      Notifications.showNotificationError(
        t('error'),
        t('ERROR_PAYMENT_DENIED.BANCONTACT'),
      );
    }
  }, []);
  // client side (browser)
  const handleStripePayment = async (paymentMethod, secret) =>
    StripeService.handlePaymentConfirmation(paymentMethod, secret);

  const stripe = useStripe();
  const elements = useElements();

  const handleBancontactPayment = async (
    accountHolderNameParam,
    client_secret,
    orderId,
  ) => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return null;
    }

    const result = await stripe.confirmBancontactPayment(client_secret, {
      payment_method: {
        billing_details: {
          name: accountHolderNameParam,
        },
      },
      return_url: `${window.location.origin}${window.location.pathname}?orderId=${orderId}`,
    });
    // console.log(`*** StripeService.handleBancontactPayment:(${orderId}).result: `, result);
    const { error } = result;
    if (error) {
      throw { error };
    }
    return result;
  };
  const updatePaymentIntent = (orderId, paymentMethod) => {
    let method = 'post';
    let url = `/api/payment/intent/academy/order/${orderId}/payment_method`;
    if (settings?.paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES) {
      method = 'put';
      url = `/api/academy/order/${orderId}/payment/method`;
    }
    const params = {
      paymentMethod: tabActive,
    };
    if (
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD ||
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD_SOURCE
    ) {
      params.paymentMethodId = selectedCreditCard;
    }
    axios[method](url, params, {
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'License',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    })
      .then(async ({ data: response }) => {
        const { client_secret, id } = response.data;
        if (paymentMethod === 'credit_card') {
          await handleStripePayment(selectedCreditCard, client_secret);
          await confirmPayment(id, orderId, paymentMethod);
        } else if (paymentMethod === PAYMENT_METHOD.BANCONTACT) {
          await handleBancontactPayment(
            accountHolderName,
            client_secret,
            orderId,
          );
        } else if (
          paymentMethod === PAYMENT_METHOD.PAID_BY_COMPANY ||
          paymentMethod === PAYMENT_METHOD.BANK_TRANSFER
        ) {
          await confirmPayment(id, orderId, paymentMethod);
        } else if (paymentMethod === PAYMENT_METHOD.PAYPAL) {
          await handlePaypalPayment(
            stripe,
            () => {},
            null,
            client_secret,
            orderId,
            `${window.location.origin}${window.location.pathname}?orderId=${orderId}`,
          );
        }
        setIsLoading(prevState => ({ ...prevState, modal: false }));
        await handleClose();
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'ERROR_PAYMENT_DENIED');
        console.log(err);
        setIsLoading(prevState => ({ ...prevState, modal: false }));
      });
  };
  const capturePayment = (
    orderId,
    paymentMethod = 'credit_card',
    paymentMethodId,
  ) => {
    let url = `/api/payment/intent/academy/order/${orderId}/capture`;
    if (settings?.paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES) {
      url = `/api/academy/order/${orderId}/capture_payment`;
    }
    let params = {
      paymentMethod,
    };
    if (paymentMethod === 'credit_card') {
      params.paymentMethodId = paymentMethodId;
    }
    // If price is Zero the payment method should be none so the client could buy the item without going to confirm payment
    if (checkoutDetail.total === 0) {
      params = {
        paymentMethod: 'bank_transfer_manual',
      };
    }
    if (paymentMethod === PAYMENT_METHOD.PAYPAL && params.paymentMethodId) {
      delete params.paymentMethodId;
    }

    axios
      .post(url, params, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'License',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(async ({ data: response }) => {
        const { client_secret, id } = response.data;
        if (
          !client_secret &&
          !id &&
          params.paymentMethod === 'bank_transfer_manual'
        ) {
          Notifications.showNotificationSuccess(
            t('success'),
            t('order_create_success'),
          );
          await confirmPayment(id, orderId, paymentMethod);
          // onClose();
          setTimeout(() => {
            window.location = `${window.location.origin}${window.location.pathname}`;
          }, 2000);
          return;
        }
        if (paymentMethod === PAYMENT_METHOD.CREDIT_CARD) {
          await handleStripePayment(paymentMethodId, client_secret);
          await confirmPayment(id, orderId, paymentMethod);
        } else if (paymentMethod === PAYMENT_METHOD.BANCONTACT) {
          await handleBancontactPayment(
            accountHolderName,
            client_secret,
            orderId,
          );
        } else if (paymentMethod === PAYMENT_METHOD.PAYPAL) {
          await handlePaypalPayment(
            stripe,
            () => {},
            null,
            client_secret,
            orderId,
            `${window.location.origin}${window.location.pathname}?orderId=${orderId}`,
          );
        } else {
          await confirmPayment(id, orderId, paymentMethod);
        }
        setIsLoading(prevState => ({ ...prevState, modal: false }));
        await handleClose();
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'ERROR_PAYMENT_DENIED');
        console.log(err);
        setIsLoading(prevState => ({ ...prevState, modal: false }));
      });
  };

  /** API REQUESTS - Create Order */
  const createOrderRequest = async () => {
    if (tabActive === 'bancontact') {
      if (!(accountHolderName && accountHolderName.length >= 3)) {
        Notifications.showNotificationError(
          t('error'),
          t('ERROR_BANCONTACT_INVALID'),
        );
      }
    }
    const { isPaidWithCoins, discount } = checkDiscountToApply(
      checkoutAcademyCoupon,
    );
    const orderData = {
      [field]: [
        {
          [fieldId]: item._id,
          quantity:
            field === 'sessions' && targetUsers ? targetUsers.length : 1,
        },
      ],
      paymentMethodType: isPaidWithCoins
        ? PAYMENT_METHOD.BANK_TRANSFER
        : tabActive,
      country,
      currency,
      isPaidWithCoins,
      discount,
    };

    if (field === 'sessions' && targetUsers.length) {
      orderData.targetUsers = targetUsers;
    }

    if (tabActive === PAYMENT_METHOD.PAID_BY_COMPANY) {
      orderData.companyBilling = {
        ...companyBilling,
        ...companyEmails,
      };
      delete orderData.paymentId;
    } else {
      orderData.billingAddress = checkoutAcademyAddress.billingAddress;
    }

    if (notes && notes.length > 0) {
      orderData.notes = notes;
    }
    // In the case of order total is 0 we deleted the paymentId, not needed
    if (checkoutDetail.total === 0) {
      delete orderData.paymentId;
    }

    const url = '/api/academy/order';
    axios
      .post(url, orderData, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(async ({ data: response }) => {
        // console.log('*CreateOrderRequest - RESPONSE', response.data);
        const { _id } = response.data;
        setOrderCreated(_id);
        capturePayment(_id, orderData.paymentMethodType, selectedCreditCard);
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'order_create_fail');
        console.log(err);
        setIsLoading(prevState => ({ ...prevState, modal: false }));
        onClose();
      });
  };

  const createOrder = async () => {
    try {
      setIsLoading(prevState => ({ ...prevState, modal: true }));
      if (orderCreated) {
        await updatePaymentIntent(orderCreated, tabActive);
      } else {
        await createOrderRequest();
      }
    } catch (err) {
      console.log(err);
      setIsLoading(prevState => ({ ...prevState, modal: false }));
    }
  };

  const isDisabled =
    !terms ||
    (tabActive !== PAYMENT_METHOD.PAID_BY_COMPANY &&
      !billingAddress?.address_1) || // If there is no address
    (tabActive === 'credit_card' && !selectedCreditCard) || // if tab active is credit card and there is no card
    (tabActive === 'bancontact' &&
      (!accountHolderName || accountHolderName.length < 3)) || // not account holder name inserted
    (tabActive === PAYMENT_METHOD.PAID_BY_COMPANY &&
      !companyBilling?.address?.address_1) || // not account holder name inserted
    showAddressForm || // Form open
    isLoading.modal ||
    isLoading.sidebar || // if is loading
    settings?.paymentProviderType === PAYMENT_PROVIDER.NONE;

  const renderSteps = () => {
    switch (step) {
      case 3:
        return (
          <CheckoutPayment
            item={item}
            countries={countries}
            vatTypes={vatTypes}
            tenant={tenant}
            companyBilling={{
              ...companyBilling,
              emails: [companyEmails.email, companyEmails?.emails],
            }}
            handleIsLoading={handleIsLoading}
            checkoutDetail={checkoutDetail}
            // PAYMENT PROPS
            cards={userCreditCards}
            tabActive={tabActive}
            selectedCreditCard={selectedCreditCard}
            accountHolderName={accountHolderName}
            updatePaymentMethod={updatePaymentMethod}
            // COUPON
            couponInserted={couponInserted}
            updateCoupon={updateCoupon}
            selectedCoupon={selectedCoupon}
            coupons={coupons}
            orderAlreadyCreated={orderCreated}
          />
        );
      case 2:
        return (
          <div className="academy-payment-address col-12 shipping-toPayContainer">
            <BillingPaymentOptions
              billingAddress={billingAddress}
              handleAddressForm={handleAddressForm}
              orderAlreadyCreated={orderCreated}
              tenant={tenant}
              showAddressForm={showAddressForm}
              saveAddress={saveAddress}
              countries={countries}
              user={loggedUser}
              handleAddressChange={handleAddressChange}
              vatTypes={vatTypes}
              billingActive={billingActive}
              onBillingChange={handleBillingChange}
              onCompanyBillingChange={handleChangeCompanyBilling}
              companyBilling={companyBilling}
              errors={errors}
              companyEmails={companyEmails}
              onCompanyEmailsChange={handleCompanyEmails}
              isOnlyPaidByCompany={isOnlyPaidByCompany}
            />
          </div>
        );
      case 1:
      default:
        return (
          <AcademyOrderDetail
            item={item}
            checkoutDetail={checkoutDetail}
            participants={
              (field === 'sessions' && item.mode === 'public' && targetUsers) ||
              null
            }
            targetUsers={targetUsers}
            onTargetEmailsChange={handleTargetEmailsChange}
            errors={errors}
            typeOfItem={typeOfItem}
            onClose={onClose}
            currentUserBoughtSession={currentUserBoughtSession}
          />
        );
    }
  };

  return (
    <Modal onClose={handleClose} id="academyPayment">
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content row academy-payment">
          <div className="col-lg-8 col-12 remove-padding-col first-section">
            <button
              type="button"
              className="close close-checkout-modal hide-inBigger"
              onClick={onClose}
            >
              <span aria-hidden="true">×</span>
            </button>
            <AcademyPaymentWizard activeStep={step} />
            {renderSteps()}
          </div>
          <div
            id="academySideBar"
            className="col-lg-4 col-12 remove-padding-col"
          >
            <AcademySideBar
              checkoutDetail={checkoutDetail}
              onClearCoupon={handleClearCoupon}
              onChangeNotes={handleChangeNotes}
              createOrder={createOrder}
              onClose={handleClose}
              isDisabled={
                (checkoutDetail.total > 0 ? isDisabled : false) || !terms
              }
              orderAlreadyCreated={orderCreated}
              checkedTerms={terms}
              onChangeTerms={handleTerms}
              tenant={tenant}
              isDirectSellingActive={isDirectSellingActive}
              onChangeStep={handleStepChange}
              step={step}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

AcademyPaymentModal.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  typeOfItem: PropTypes.shape({
    fieldId: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    userPrice: PropTypes.shape(PriceProp.PropTypes),
    userPromotionPrice: PropTypes.shape(PriceProp.PropTypes),
    name: PropTypes.string.isRequired,
    image: PropTypes.shape(ImageProp.propType).isRequired,
    mode: PropTypes.string,
  }).isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  orderId: PropTypes.string,
  redirectStatus: PropTypes.string,
  paymentIntent: PropTypes.string,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  openSuccessModal: PropTypes.func.isRequired,
};

AcademyPaymentModal.defaultProps = {
  orderId: null,
  redirectStatus: null,
  paymentIntent: null,
};

export default compose(translate, withCSRF)(AcademyPaymentModal);
